import {
  archiveDesignCss,
  archiveGallery,
  archiveGalleryStyle,
  validateArchiveGallery,
} from "./archive-design";
import { validateEditorGuides } from "./page-guides";
import { validatePageLayers } from "./page-layers";
import { compactAudioCss, renderMediaPlayer } from "./media-player";
export const contexts = [
  "reading",
  "empty",
  "archive",
  "archiveEmpty",
  "archiveReading",
  "archiveCard",
] as const;
export type LayoutContext = (typeof contexts)[number];
export const blockLabels: Record<string, string> = {
  brand: "Church name",
  series: "Journey heading",
  day: "Day number",
  title: "Reading title",
  subtitle: "Reading subtitle",
  content: "Reading / media",
  mediaLink: "Media fallback link",
  emptyLabel: "Upcoming label",
  emptyTitle: "Upcoming heading",
  emptyText: "Upcoming message",
  archiveList: "Previous readings",
  archiveType: "Reading type label",
  archiveDate: "Publication date",
  archiveOpen: "Open reading button",
  navigation: "Page navigation",
  status: "Update message",
  close: "Close reading",
  text: "Custom text",
  button: "Custom button",
  image: "Image",
  divider: "Divider",
  spacer: "Spacer",
  rectangle: "Rectangle",
  ellipse: "Ellipse",
  triangle: "Triangle",
  line: "Line",
};
export interface BlockStyle {
  width: number;
  x: number;
  y?: number;
  edgePadding?: number;
  minHeight?: number;
  align: "left" | "center" | "right";
  size: number;
  lineHeight: number;
  weight: number;
  font: string;
  color: string;
  gap: number;
  padding: number;
  radius: number;
  button: "solid" | "outline";
  buttonFill?: string;
  buttonHoverFill?: string;
  buttonTextColor?: string;
  buttonHoverTextColor?: string;
  strokeWidth?: number;
  strokeColor?: string;
  strokeStyle?: "solid" | "dashed" | "dotted";
  fill?: string;
}
export interface PageBlock {
  positionMode?: "flow" | "pinned";
  safeBox?: { desktop: SafeBox; mobile: SafeBox };
  mobileMode?: "auto" | "custom";
  id: string;
  kind: string;
  hidden: boolean;
  text: string;
  href: string;
  src: string;
  desktop: BlockStyle;
  mobile: BlockStyle;
}
export interface SafeBox {
  x: number;
  y: number;
  width: number;
  height: number;
}
export interface CustomFont {
  id: string;
  name: string;
  url: string;
}
export const escapeHtml = (s: unknown) =>
  String(s ?? "").replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        c
      ]!,
  );
export function createBlock(
  kind: string,
  id = `b-${crypto.randomUUID()}`,
): PageBlock {
  const size = ["series", "title", "emptyTitle"].includes(kind)
    ? 36
    : kind === "brand" || kind === "day" || kind === "emptyLabel"
      ? 12
      : 19;
  const style: BlockStyle = {
    width: 100,
    x: 50,
    y: 0,
    align: kind.startsWith("empty") ? "center" : "left",
    size,
    lineHeight: size > 24 ? 1.2 : 1.7,
    weight: kind === "day" || kind === "emptyLabel" ? 700 : 400,
    font: ["series", "title", "emptyTitle"].includes(kind)
      ? "heading"
      : kind === "content"
        ? "serif"
        : "sans",
    color: kind === "day" || kind === "emptyLabel" ? "accent" : "text",
    gap: 18,
    padding: 0,
    radius: 20,
    button: "solid",
    strokeWidth: 1,
    strokeColor: "primary",
    strokeStyle: "solid",
    fill: "transparent",
  };
  return {
    mobileMode: "auto",
    id,
    kind,
    hidden: false,
    text:
      (
        {
          brand: "PINEDALE CHRISTIAN CHURCH",
          emptyLabel: "A NEW JOURNEY AWAITS",
          emptyTitle: "Make room for a daily moment.",
          text: "Your text",
          button: "Learn more",
          close: "Close ×",
        } as Record<string, string>
      )[kind] || "",
    href: "",
    src: "",
    desktop: { ...style },
    mobile: { ...style, size: Math.min(size, 30) },
  };
}
export function defaultBlocks(context: LayoutContext): PageBlock[] {
  const kinds =
    context === "reading"
      ? [
          "brand",
          "series",
          "divider",
          "day",
          "title",
          "subtitle",
          "content",
          "mediaLink",
          "divider",
          "navigation",
          "status",
        ]
      : context === "empty"
        ? [
            "brand",
            "series",
            "divider",
            "emptyLabel",
            "emptyTitle",
            "emptyText",
            "spacer",
            "divider",
            "navigation",
            "status",
          ]
        : context === "archive"
          ? [
              "brand",
              "series",
              "divider",
              "archiveList",
              "divider",
              "navigation",
              "status",
            ]
          : context === "archiveEmpty"
            ? [
                "brand",
                "series",
                "divider",
                "emptyTitle",
                "emptyText",
                "divider",
                "navigation",
                "status",
              ]
            : context === "archiveCard"
              ? ["day", "title", "archiveType", "archiveDate", "archiveOpen"]
              : ["close", "day", "title", "subtitle", "content", "mediaLink"];
  return kinds.map((kind, i) => {
    const b = createBlock(kind, `${context}-${i}`);
    if (context === "archiveEmpty" && kind === "emptyTitle")
      b.text = "A journey worth revisiting.";
    if (kind === "divider") b.desktop.gap = b.mobile.gap = 28;
    if (kind === "spacer") b.desktop.gap = 48;
    if (kind === "close") b.desktop.align = b.mobile.align = "right";
    if (context === "archiveCard") {
      for (const d of ["desktop", "mobile"] as const) {
        b[d].size = kind === "title" ? 21 : 13;
        b[d].gap = 12;
      }
    }
    return b;
  });
}
function hasCustomMobile(block: PageBlock): boolean {
  const fallback = createBlock(block.kind, block.id).mobile;
  const keys: (keyof BlockStyle)[] = [
    "width",
    "x",
    "align",
    "size",
    "font",
    "weight",
    "color",
    "gap",
    "padding",
    "lineHeight",
    "radius",
    "button",
  ];
  const legacyCustom =
    block.mobileMode === undefined &&
    (keys.some((key) => block.mobile[key] !== fallback[key]) ||
      (block.mobile.y ?? 0) !== 0 ||
      (block.mobile.minHeight ?? 0) !== 0 ||
      (block.mobile.edgePadding ?? 0) !== 0);
  return block.mobileMode === "custom" || legacyCustom;
}
export function mobileStyle(block: PageBlock): BlockStyle {
  if (hasCustomMobile(block)) return { ...block.mobile };
  return {
    ...block.desktop,
    width: 100,
    x: 50,
    y: 0,
    minHeight: 0,
    size: Math.min(block.desktop.size, 32),
    padding: Math.min(block.desktop.padding, 24),
    edgePadding: Math.min(block.desktop.edgePadding ?? 0, 8),
    gap: Math.min(block.desktop.gap, 48),
  };
}
export function themeBlocks(
  theme: any,
  context: LayoutContext,
  device = "desktop",
): PageBlock[] {
  const blocks: PageBlock[] =
    (device === "mobile" ? theme.mobileLayouts?.[context] : undefined) ??
    theme.layouts?.[context] ??
    defaultBlocks(context).map((b) => {
      b.desktop.button = theme.buttonDesktop ?? theme.button ?? "solid";
      b.mobile.button = theme.buttonMobile ?? theme.button ?? "solid";
      if (theme.buttonMobile !== undefined) b.mobileMode = "custom";
      b.desktop.radius = b.mobile.radius = theme.radius ?? 20;
      return b;
    });
  return blocks.map((b) => ({
    ...b,
    mobileMode: hasCustomMobile(b) ? "custom" : "auto",
    mobile: mobileStyle(b),
  }));
}
const validMedia = (s: string) => /^\/media\/[a-f0-9-]+$/.test(s);
export function validateLayout(v: any): any {
  const fail = (condition: unknown, message: string) => {
    if (!condition) throw Error(message);
  };
  const fonts: CustomFont[] = v.fonts ?? [];
  for (const key of ["buttonDesktop", "buttonMobile"])
    fail(
      v[key] === undefined || ["solid", "outline"].includes(v[key]),
      "Invalid device button style",
    );
  fail(
    Array.isArray(fonts) && fonts.length <= 20,
    "Use at most 20 custom fonts",
  );
  const ids = new Set<string>();
  for (const f of fonts) {
    fail(
      f && /^[a-zA-Z0-9-]{1,64}$/.test(f.id) && !ids.has(f.id),
      "Invalid font ID",
    );
    ids.add(f.id);
    fail(
      typeof f.name === "string" &&
        f.name.trim().length > 0 &&
        f.name.length <= 100 &&
        validMedia(f.url),
      "Invalid custom font",
    );
  }
  const number = (value: any, min: number, max: number, name: string) => {
    fail(
      typeof value === "number" &&
        Number.isFinite(value) &&
        value >= min &&
        value <= max,
      `Invalid ${name}`,
    );
    return value as number;
  };
  const style = (s: any): BlockStyle => {
    fail(
      ["solid", "dashed", "dotted"].includes(s?.strokeStyle ?? "solid"),
      "Invalid stroke style",
    );
    for (const key of ["strokeColor", "fill"]) {
      const value = s?.[key] ?? (key === "fill" ? "transparent" : "primary");
      fail(
        ["text", "accent", "primary", "inherit", "transparent"].includes(
          value,
        ) || /^#[\da-f]{6}$/i.test(value),
        `Invalid ${key}`,
      );
    }
    fail(
      s && ["left", "center", "right"].includes(s.align),
      "Invalid element alignment",
    );
    fail(
      ["serif", "sans", "heading", ...fonts.map((f) => f.id)].includes(s.font),
      "Select an installed font",
    );
    fail(
      ["text", "accent", "primary", "inherit"].includes(s.color) ||
        /^#[\da-f]{6}$/i.test(s.color),
      "Invalid element color",
    );
    fail(
      ["solid", "outline"].includes(s.button),
      "Invalid element button style",
    );
    const buttonColors: Partial<BlockStyle> = {};
    for (const key of [
      "buttonFill",
      "buttonHoverFill",
      "buttonTextColor",
      "buttonHoverTextColor",
    ] as const) {
      if (s[key] === undefined) continue;
      fail(
        [
          "default",
          "primary",
          "secondary",
          "text",
          "accent",
          "surface",
        ].includes(s[key]) ||
          ((key === "buttonFill" || key === "buttonHoverFill") &&
            s[key] === "transparent") ||
          /^#[\da-f]{6}$/i.test(s[key]),
        "Invalid " + key,
      );
      buttonColors[key] = s[key];
    }
    return {
      ...buttonColors,
      width: number(s.width, 10, 100, "element width"),
      x: number(s.x, 0, 100, "element position"),
      y: number(s.y ?? 0, -100000, 100000, "vertical position"),
      edgePadding: number(s.edgePadding ?? 0, 0, 20, "edge padding"),
      minHeight: number(s.minHeight ?? 0, 0, 200, "element minimum height"),
      align: s.align,
      size: number(s.size, 10, 100, "font size"),
      lineHeight: number(s.lineHeight, 1, 3, "line height"),
      weight: number(s.weight, 100, 900, "font weight"),
      font: s.font,
      color: s.color,
      gap: number(s.gap, 0, 800, "element spacing"),
      padding: number(s.padding, 0, 120, "element padding"),
      radius: number(s.radius, 0, 100, "element radius"),
      button: s.button,
      strokeWidth: number(s.strokeWidth ?? 1, 0, 40, "stroke width"),
      strokeColor: s.strokeColor ?? "primary",
      strokeStyle: s.strokeStyle ?? "solid",
      fill: s.fill ?? "transparent",
    };
  };
  const layouts: Partial<Record<LayoutContext, PageBlock[]>> = {};
  fail(
    !v.layouts || (typeof v.layouts === "object" && !Array.isArray(v.layouts)),
    "Invalid layouts",
  );
  for (const context of contexts) {
    if (v.layouts?.[context] === undefined) continue;
    const blocks = v.layouts[context];
    fail(
      Array.isArray(blocks) && blocks.length <= 60,
      "Use at most 60 elements per layout",
    );
    const seen = new Set<string>();
    layouts[context] = blocks.map((b: any) => {
      fail(
        b &&
          typeof b.id === "string" &&
          /^[a-zA-Z0-9-]{1,80}$/.test(b.id) &&
          !seen.has(b.id),
        "Invalid element ID",
      );
      seen.add(b.id);
      fail(
        Object.hasOwn(blockLabels, b.kind) && typeof b.hidden === "boolean",
        "Invalid page element",
      );
      fail(
        b.mobileMode === undefined || ["auto", "custom"].includes(b.mobileMode),
        "Invalid mobile layout mode",
      );
      fail(
        !(
          context === "archiveCard" &&
          ["archiveList", "content"].includes(b.kind)
        ),
        "A reading card cannot contain another reading list or full reading content",
      );
      fail(
        typeof b.text === "string" && b.text.length <= 5000,
        "Element text is too long",
      );
      fail(
        typeof b.href === "string" &&
          b.href.length <= 2000 &&
          (!b.href ||
            /^https:\/\/[^\s"<>]+$/.test(b.href) ||
            /^\/(?!\/)[^\s"<>\\]*$/.test(b.href)),
        "Use an HTTPS link or a site path",
      );
      if (b.href)
        fail(
          !new URL(b.href, "https://pinedale.church").username &&
            !new URL(b.href, "https://pinedale.church").password,
          "Links cannot contain credentials",
        );
      fail(
        typeof b.src === "string" &&
          (!b.src ||
            validMedia(b.src) ||
            /^\/art\/[a-z0-9-]+\.(webp|png|svg)$/.test(b.src)),
        "Upload an element image first",
      );
      return {
        ...(b.safeBox
          ? {
              safeBox: Object.fromEntries(
                ["desktop", "mobile"].map((device) => {
                  const box = b.safeBox[device];
                  fail(box && typeof box === "object", "Invalid safe box");
                  const group =
                    b.kind === "archiveList" ||
                    b.id === "archiveCard-background";
                  const rect = {
                    x: number(box.x, -1000, 1000, "safe box left"),
                    y: number(box.y, -1000, 1000, "safe box top"),
                    width: number(
                      box.width,
                      1,
                      group ? 400 : 100,
                      "safe box width",
                    ),
                    height: number(
                      box.height,
                      1,
                      group ? 400 : 100,
                      "safe box height",
                    ),
                  };
                  return [device, rect];
                }),
              ) as PageBlock["safeBox"],
            }
          : {}),
        ...(b.positionMode === "flow" || b.positionMode === "pinned"
          ? { positionMode: b.positionMode }
          : {}),
        mobileMode: b.mobileMode,
        id: b.id,
        kind: b.kind,
        hidden: b.hidden,
        text: b.text,
        href: b.href,
        src: b.src,
        desktop: style(b.desktop),
        mobile: style(b.mobile),
      };
    });
  }
  return {
    layouts,
    ...(v.editorGuides === undefined
      ? {}
      : { editorGuides: validateEditorGuides(v.editorGuides, contexts) }),
    ...(v.archiveGallery === undefined
      ? {}
      : { archiveGallery: validateArchiveGallery(v.archiveGallery) }),
    ...(v.pageLayers === undefined
      ? {}
      : { pageLayers: validatePageLayers(v.pageLayers) }),
    buttonDesktop: v.buttonDesktop ?? v.button ?? "solid",
    buttonMobile: v.buttonMobile ?? v.button ?? "solid",
    ...(v.mobileLayouts === undefined
      ? {}
      : {
          mobileLayouts: validateLayout({
            ...v,
            layouts: v.mobileLayouts,
            mobileLayouts: undefined,
          }).layouts,
        }),
    artworkLayout:
      v.artworkLayout === true ||
      (v.artworkLayout === undefined &&
        v.opacity === 0 &&
        v.panelBorder === false &&
        v.panelShadow === false),
    artworkReferenceWidth: number(
      v.artworkReferenceWidth ?? 1440,
      600,
      4000,
      "artwork reference width",
    ),
    fonts: fonts.map((f) => ({ id: f.id, name: f.name.trim(), url: f.url })),
    panelWidth: number(v.panelWidth ?? 820, 240, 2000, "reading box width"),
    panelPadding: number(v.panelPadding ?? 64, 0, 160, "reading box padding"),
    panelMobilePadding: number(
      v.panelMobilePadding ?? 22,
      0,
      80,
      "mobile box padding",
    ),
    panelDesktopHeight: number(
      v.panelDesktopHeight ?? 0,
      0,
      200,
      "desktop box minimum height",
    ),
    panelMobileHeight: number(
      v.panelMobileHeight ?? 0,
      0,
      200,
      "mobile box minimum height",
    ),
    panelDesktopFreeX: number(
      v.panelDesktopFreeX ?? 0,
      -1000,
      1000,
      "desktop free box position",
    ),
    panelMobileFreeX: number(
      v.panelMobileFreeX ?? 0,
      -1000,
      1000,
      "mobile free box position",
    ),
    panelMobileX: number(
      v.panelMobileX ?? v.panelX ?? 50,
      0,
      100,
      "mobile box horizontal position",
    ),
    panelDesktopWidth: number(
      v.panelDesktopWidth ?? 100,
      10,
      100,
      "desktop box width",
    ),
    panelMobileWidth: number(
      v.panelMobileWidth ?? 100,
      10,
      100,
      "mobile box width",
    ),
    panelDesktopOffset: number(
      v.panelDesktopOffset ?? 0,
      -100,
      200,
      "desktop box position",
    ),
    panelMobileOffset: number(
      v.panelMobileOffset ?? 0,
      -100,
      200,
      "mobile box position",
    ),
    panelBorder: v.panelBorder !== false,
    panelShadow: v.panelShadow !== false,
  };
}
export const mediaPlayerCss =
  compactAudioCss +
  ".media-player,.layout-canvas iframe.media-player{display:block;width:100%;max-width:100%;height:auto;min-height:0;aspect-ratio:16/9;border:0;overflow:hidden}.image-safe-box:has(.media-player){overflow:visible}.image-safe-box:has(.media-player)>.page-blocks{overflow:hidden}.image-safe-box .page-block>.media-player{height:100%;min-height:0;aspect-ratio:auto}";

const buttonInteractionCss = `.page-block .layout-button:hover,.layout-canvas .page-block .layout-button:hover{background:var(--button-hover-bg,var(--button-bg));color:var(--button-hover-color,var(--button-color))}@media(max-width:640px){.page-block .layout-button:hover,.layout-canvas .page-block .layout-button:hover{background:var(--mobile-button-hover-bg,var(--mobile-button-bg));color:var(--mobile-button-hover-color,var(--mobile-button-color))}}.layout-canvas.mobile .page-block .layout-button:hover{background:var(--mobile-button-hover-bg,var(--mobile-button-bg));color:var(--mobile-button-hover-color,var(--mobile-button-color))}`;
export const layoutCss =
  mediaPlayerCss +
  buttonInteractionCss +
  `.layout-scene,.scene{box-sizing:border-box;width:100%;min-width:0;aspect-ratio:var(--artwork-aspect,16/9);padding-top:0!important;padding-bottom:0!important}.layout-scene>.layout-panel,.scene>.panel{position:absolute;transform:translateX(calc(var(--reading-free-x,0)*1cqw));top:calc(var(--reading-top,0px)*var(--artwork-scale,1) + var(--reading-offset,0)*1cqw + var(--artwork-offset,0px));left:0}` +
  `.page-blocks{display:flex;flex-direction:column;min-width:0;container-type:inline-size}.page-block{box-sizing:border-box;width:min(var(--bw),calc(100% - 2 * var(--bedge,0%)));margin-left:calc(var(--bedge,0%) + (100% - 2 * var(--bedge,0%) - min(var(--bw),calc(100% - 2 * var(--bedge,0%))))*var(--bx));min-height:calc(var(--bh,0) * 1cqw);margin-bottom:var(--bg);padding:var(--bp);text-align:var(--ba);color:var(--bc);font-family:var(--bf);font-size:var(--bs);font-weight:var(--bweight);line-height:var(--bl);overflow-wrap:anywhere;min-width:0;position:relative;transform:translateY(var(--by,0%))}.page-block h1,.page-block h2,.page-block p,.page-block .prose,.page-block .subtitle,.page-block .eyebrow,.page-block .brand{font:inherit;color:inherit;text-align:inherit;margin:0}.page-block .prose p+p{margin-top:1em}.page-block .prose h2,.page-block .prose h3{font-size:1.3em}.page-block nav{margin:0;padding:0;border:0}.page-block .layout-button,.layout-canvas .page-block .layout-button{font:inherit;border-radius:var(--br);background:var(--button-bg);color:var(--button-color);border:1px solid var(--primary);display:inline-flex;align-items:center;justify-content:center;min-height:44px;padding:12px 22px;text-decoration:none;cursor:pointer;max-width:100%;white-space:normal}.page-block .layout-image{max-width:100%;height:auto;display:block;margin:0 auto}.page-block hr{border:0;border-top:1px solid currentColor;opacity:.2;margin:0}.page-block .card{font:inherit}.card>.page-blocks{width:100%}.page-block .card strong,.page-block .card .eyebrow,.page-block .card small{font:inherit;color:inherit}.page-block .card small{font-size:.75em}.page-block .card{color:inherit}.page-block .status:empty{display:none}@media(max-width:640px){.page-block{width:min(var(--mw),calc(100% - 2 * var(--medge,0%)));margin-left:calc(var(--medge,0%) + (100% - 2 * var(--medge,0%) - min(var(--mw),calc(100% - 2 * var(--medge,0%))))*var(--mx));min-height:calc(var(--mh,0) * 1cqw);margin-bottom:var(--mg);padding:var(--mp);text-align:var(--ma);font-size:var(--ms);font-family:var(--mf);font-weight:var(--mweight);line-height:var(--ml);color:var(--mc);transform:translateY(var(--my,0%))}.page-block .layout-button,.layout-canvas .page-block .layout-button{border-radius:var(--mr);background:var(--mobile-button-bg);color:var(--mobile-button-color)}}`;
function fontFamily(font: string, theme: any) {
  if (font === "heading") font = theme.heading;
  if (font === "serif") return "Georgia,serif";
  if (font === "sans") return "system-ui,sans-serif";
  return theme.fonts?.some((f: CustomFont) => f.id === font)
    ? `pd-${font},sans-serif`
    : "system-ui,sans-serif";
}
export const safeBoxCss =
  `
.safe-box-layer{position:absolute;top:var(--artwork-offset,0px);left:0;width:100%;aspect-ratio:var(--artwork-aspect,16/9);pointer-events:none;z-index:var(--layer-order,2)}
.image-safe-box{position:absolute;box-sizing:border-box;isolation:isolate;z-index:1;left:var(--safe-x);top:var(--safe-y);width:var(--safe-w);height:var(--safe-h);pointer-events:auto;overflow:auto;padding:0;background:transparent}
.image-safe-box>.page-blocks{height:100%}
.image-safe-box>.page-blocks>.page-block{box-sizing:border-box;width:100%!important;height:100%;margin:0!important;transform:none!important;min-height:0!important}
.image-safe-box>.page-blocks>.page-block>.layout-button{box-sizing:border-box;width:100%;height:100%;min-height:0;padding:var(--bp)}
.image-safe-box[data-shape]{overflow:visible}
.image-safe-box[data-editing]{outline:1px dashed #0076ab;touch-action:none;cursor:move}
.image-safe-box[data-layer-selected]{outline:2px solid #0076ab;outline-offset:1px}
.safe-box-resize,.safe-box-edge{position:absolute;z-index:25;min-height:0!important;width:10px;height:10px;padding:0!important;border:1px solid white!important;border-radius:1px!important;background:#0076ab!important;transform:translate(-50%,-50%);touch-action:none;opacity:0;pointer-events:none}
.safe-box-resize{right:-5px;bottom:-5px;cursor:nwse-resize}
.image-safe-box[data-layer-selected]>.safe-box-edge,.image-safe-box[data-layer-selected]>.safe-box-resize{opacity:1;pointer-events:auto}
.page-content{box-sizing:border-box;min-width:0;max-width:100%;position:absolute;inset:0;padding:calc(24px*var(--artwork-scale,1));pointer-events:none}.page-content>.page-blocks,.page-content>.device-layout-desktop>.page-blocks,.page-content>.device-layout-mobile>.page-blocks{pointer-events:auto}
@media(max-width:640px){.image-safe-box{left:var(--safe-mx);top:var(--safe-my);width:var(--safe-mw);height:var(--safe-mh)}}
.safe-box-layer[data-device="mobile"]>.image-safe-box{left:var(--safe-mx);top:var(--safe-my);width:var(--safe-mw);height:var(--safe-mh)}
.safe-box-layer[data-device="desktop"]>.image-safe-box{left:var(--safe-x);top:var(--safe-y);width:var(--safe-w);height:var(--safe-h)}
` + archiveDesignCss;
export const deviceLayoutCss = `.device-layout-mobile{display:none}@media(max-width:640px){.device-layout-desktop{display:none}.device-layout-mobile{display:contents}}.page-block{--active-stroke:var(--bstroke,1px);--active-stroke-color:var(--bstroke-color,var(--primary));--active-stroke-style:var(--bstroke-style,solid);--active-fill:var(--bfill,transparent)}.page-block .layout-button,.page-block .layout-image{border:var(--active-stroke) var(--active-stroke-style) var(--active-stroke-color)}.page-block hr{border-top:var(--active-stroke) var(--active-stroke-style) var(--active-stroke-color);opacity:1}.layout-shape{display:block;width:100%;height:100%;min-height:24px;overflow:visible;stroke-width:var(--active-stroke);stroke:var(--active-stroke-color);fill:var(--active-fill)}.layout-shape *{vector-effect:non-scaling-stroke}.layout-shape rect{rx:var(--br)}@media(max-width:640px){.layout-shape rect{rx:var(--mr)}}.image-safe-box[data-shape]{padding:0;background:transparent;overflow:visible}.image-safe-box[data-shape]>.page-blocks,.image-safe-box[data-shape]>.page-blocks>.page-block{height:100%;margin:0}.page-block .layout-shape{stroke-dasharray:var(--stroke-dash,none)}@media(max-width:640px){.page-block{--active-stroke:var(--mstroke,1px);--active-stroke-color:var(--mstroke-color,var(--primary));--active-stroke-style:var(--mstroke-style,solid);--active-fill:var(--mfill,transparent)}}`;
function pinnedHandles(block: PageBlock, label: string, gallery: boolean) {
  const sides = gallery
    ? [
        ["l", 0, 50],
        ["r", 100, 50],
      ]
    : [
        ["l", 0, 50],
        ["r", 100, 50],
        ["t", 50, 0],
        ["b", 50, 100],
        ["tl", 0, 0],
        ["tr", 100, 0],
        ["bl", 0, 100],
      ];
  return (
    (gallery || block.id === "archiveCard-background"
      ? `<button class="archive-group-handle" data-group-move>${escapeHtml(label)} · drag to move</button>`
      : "") +
    (gallery
      ? ""
      : '<button class="safe-box-resize" aria-label="Resize safe box" data-safe-edge="br"></button>') +
    sides
      .map(
        ([side, x, y]) =>
          `<button class="safe-box-edge" data-safe-edge="${side}" aria-label="Resize ${escapeHtml(label)} ${side}" style="left:${x}%;top:${y}%;cursor:${side === "l" || side === "r" ? "ew-resize" : side === "t" || side === "b" ? "ns-resize" : side === "tr" || side === "bl" ? "nesw-resize" : "nwse-resize"}"></button>`,
      )
      .join("")
  );
}
export function renderSafeBoxes(
  theme: any,
  context: LayoutContext,
  data: RenderData,
  editor = false,
  device = "",
): string {
  if (
    !device &&
    (theme.mobileLayouts?.[context] !== undefined ||
      (context === "archiveCard" &&
        themeBlocks(theme, context).some(
          (block) => block.id === "archiveCard-background" && block.safeBox,
        )))
  )
    return `<div class="device-layout-desktop">${renderSafeBoxes(theme, context, data, editor, "desktop")}</div><div class="device-layout-mobile">${renderSafeBoxes(theme, context, data, editor, "mobile")}</div>`;
  const items = themeBlocks(theme, context, device);
  if (
    context !== "archiveCard" &&
    !theme.galleryPartsIsolated &&
    items.some((item) => item.kind === "archiveList")
  ) {
    const subset = (list: PageBlock[]) => ({
      ...theme,
      galleryPartsIsolated: true,
      layouts: { ...theme.layouts, [context]: list },
      mobileLayouts: { ...theme.mobileLayouts, [context]: list },
    });
    return items
      .map((item, index) =>
        renderSafeBoxes(subset([item]), context, data, editor, device).replace(
          '<div class="safe-box-layer"',
          '<div class="safe-box-layer" style="--layer-order:' +
            (index + 2) +
            '"',
        ),
      )
      .join("");
  }
  const background =
    context === "archiveCard" &&
    items.find((b) => b.id === "archiveCard-background" && b.safeBox);
  if (background && !theme.cardPartsIsolated) {
    const active = device === "mobile" ? "mobile" : "desktop";
    const box = background.safeBox![active];
    const design = archiveGallery(theme, active);
    const subset = (list: PageBlock[]) => ({
      ...theme,
      cardPartsIsolated: true,
      layouts: { ...theme.layouts, [context]: list },
      mobileLayouts: { ...theme.mobileLayouts, [context]: list },
    });
    const normal = { ...background, safeBox: undefined };
    return `<section class="image-safe-box card-frame" data-card-frame data-shape data-block-kind="${escapeHtml(background.kind)}" data-safe-box="${escapeHtml(background.id)}" ${editor ? 'data-editing tabindex="0"' : ""} aria-label="Card background" style="--safe-x:${box.x}%;--safe-y:${box.y}%;--safe-w:${box.width}%;--safe-h:${box.height}%;--safe-flow-y:${box.y};--frame-aspect:${design.cardWidth * box.width}/${design.cardHeight * box.height};--card-reference-ratio:${design.cardHeight / design.cardWidth}"><div class="card-fill">${background.hidden ? "" : renderBlocks(subset([normal]), context, data, editor, device || "responsive")}</div><div class="card-content-anchor">${renderSafeBoxes(subset(items.filter((item) => item.id !== background.id)), context, data, editor, device)}</div>${editor ? pinnedHandles(background, "Card", false) : ""}</section>`;
  }
  return `<div class="safe-box-layer" data-device="${escapeHtml(device)}">${themeBlocks(
    theme,
    context,
    device,
  )
    .filter((b) => !b.hidden && b.safeBox)
    .map((b) => {
      const box = b.safeBox!;
      const normal = { ...b, safeBox: undefined };
      const isolated = {
        ...theme,
        layouts: { ...theme.layouts, [context]: [normal] },
      };
      const vars = (rect: SafeBox, prefix: string) =>
        `${prefix}x:${rect.x}%;${prefix}y:${rect.y}%;${prefix}w:${rect.width}%;${prefix}h:${rect.height}%;${prefix}flow-y:${rect.y}`;
      isolated.mobileLayouts = { ...theme.mobileLayouts, [context]: [normal] };
      return `<section class="image-safe-box ${b.kind === "archiveList" ? "archive-gallery" : ""}" data-block-kind="${escapeHtml(b.kind)}" data-safe-box="${escapeHtml(b.id)}" ${["rectangle", "ellipse", "triangle", "line"].includes(b.kind) ? "data-shape" : ""} ${editor ? 'data-editing tabindex="0"' : 'tabindex="0"'} aria-label="${escapeHtml(blockLabels[b.kind])} safe box" style="${vars(box.desktop, "--safe-")};${vars(box.mobile, "--safe-m")}">${renderBlocks(isolated, context, data, editor, device || "responsive")}${
        editor
          ? pinnedHandles(
              b,
              b.kind === "archiveList" ? "Card list" : blockLabels[b.kind],
              b.kind === "archiveList",
            )
          : ""
      }</section>`;
    })
    .join("")}</div>`;
}
export function fontCss(theme: any, origin: string) {
  return (theme.fonts ?? [])
    .filter(
      (f: CustomFont) => /^[a-zA-Z0-9-]{1,64}$/.test(f.id) && validMedia(f.url),
    )
    .map(
      (f: CustomFont) =>
        `@font-face{font-family:pd-${f.id};src:url("${new URL(f.url, origin).href}");font-display:swap}`,
    )
    .join("");
}
function blockStyle(b: PageBlock, theme: any, previewDevice?: string) {
  const color = (c: string) =>
    /^#[\da-f]{6}$/i.test(c) || ["inherit", "transparent"].includes(c)
      ? c
      : theme[c] || theme.text;
  return (
    (["desktop", "mobile"] as const)
      .map((device) => {
        const s =
            b[
              previewDevice === "mobile"
                ? "mobile"
                : previewDevice === "desktop"
                  ? "desktop"
                  : device
            ],
          p = device === "desktop" ? "b" : "m";
        const resolveButtonColor = (
          value: string | undefined,
          fallback: string,
        ) => (value && value !== "default" ? color(value) : fallback);
        const fill = resolveButtonColor(
          s.buttonFill,
          s.buttonFill === undefined && s.fill && s.fill !== "transparent"
            ? color(s.fill)
            : s.button === "outline"
              ? "transparent"
              : (theme.buttonBackground ?? theme.primary),
        );
        const text = resolveButtonColor(
          s.buttonTextColor,
          s.buttonTextColor === undefined && s.color.startsWith("#")
            ? s.color
            : (theme.buttonText ??
                (s.button === "outline" ? theme.primary : "#ffffff")),
        );
        const hoverFill = resolveButtonColor(s.buttonHoverFill, fill);
        const hoverText = resolveButtonColor(s.buttonHoverTextColor, text);
        const buttonPrefix = device === "desktop" ? "" : "mobile-";
        return `--${p}stroke:${s.strokeWidth ?? 1}px;--${p}stroke-color:${color(s.strokeColor ?? "primary")};--${p}stroke-style:${s.strokeStyle ?? "solid"};--${p}fill:${color(s.fill ?? "transparent")};--stroke-dash:${s.strokeStyle === "dashed" ? "8 5" : s.strokeStyle === "dotted" ? "1 4" : "none"};--${p}w:${s.width}%;--${p}x:${s.x / 100};--${p}y:${s.y ?? 0}%;--${p}edge:${s.edgePadding ?? 0}%;--${p}h:${s.minHeight ?? 0};--${p}g:${s.gap}px;--${p}p:${s.padding}px;--${p}a:${s.align};--${p}s:${s.size}px;--${p}f:${fontFamily(s.font, theme)};--${p}weight:${s.weight};--${p}l:${s.lineHeight};--${p}c:${color(s.color)};--${p}r:${s.radius}px;--${device === "desktop" ? "" : "mobile-"}button-bg:${fill};--${buttonPrefix}button-color:${text};--${buttonPrefix}button-hover-bg:${hoverFill};--${buttonPrefix}button-hover-color:${hoverText}`;
      })
      .join(";") +
    `;--stroke-dash:var(--active-stroke-dash);--bstroke-dash:${(previewDevice === "mobile" ? b.mobile : b.desktop).strokeStyle === "dashed" ? "8 5" : (previewDevice === "mobile" ? b.mobile : b.desktop).strokeStyle === "dotted" ? "1 4" : "none"};--mstroke-dash:${(previewDevice === "desktop" ? b.desktop : b.mobile).strokeStyle === "dashed" ? "8 5" : (previewDevice === "desktop" ? b.desktop : b.mobile).strokeStyle === "dotted" ? "1 4" : "none"}`
  );
}
export interface RenderData {
  series: any;
  entry?: any;
  entries?: any[];
  updated?: boolean;
  mode: "daily" | "archive";
  origin: string;
}
export function renderBlocks(
  theme: any,
  context: LayoutContext,
  data: RenderData,
  editor = false,
  device = "",
): string {
  if (!device && theme.mobileLayouts?.[context] !== undefined)
    return `<div class="device-layout-desktop">${renderBlocks(theme, context, data, editor, "desktop")}</div><div class="device-layout-mobile">${renderBlocks(theme, context, data, editor, "mobile")}</div>`;
  const e = data.entry,
    s = data.series,
    esc = escapeHtml;
  return `<div class="page-blocks">${themeBlocks(theme, context, device)
    .filter((b) => !b.hidden && !b.safeBox)
    .map((b) => {
      let html = "";
      const text = b.text;
      switch (b.kind) {
        case "brand":
          html = `<p class="brand">${esc(text)}</p>`;
          break;
        case "series":
          html = `<h1 class="series">${esc(text || s.title)}${!text && data.mode === "archive" ? " · Previous Days" : ""}</h1>`;
          break;
        case "day":
          if (e)
            html = `<div class="eyebrow">${esc(text || `DAY ${e.day} OF 40`)}</div>`;
          break;
        case "title":
          if (e) html = `<h2>${esc(text || e.title)}</h2>`;
          break;
        case "subtitle":
          if (text || e?.subtitle)
            html = `<p class="subtitle">${esc(text || e.subtitle)}</p>`;
          break;
        case "content":
          if (e)
            html =
              e.type === "media"
                ? renderMediaPlayer(e)
                : `<div class="prose">${e.content}</div>`;
          break;
        case "mediaLink":
          if (e?.type === "media")
            html = `<a href="${esc(e.content)}" target="_blank" rel="noopener">${esc(text || "Open media directly")}</a>`;
          break;
        case "emptyLabel":
          html = `<div class="eyebrow">${esc(text)}</div>`;
          break;
        case "emptyTitle":
          html = `<h2>${esc(text)}</h2>`;
          break;
        case "emptyText":
          html = `<p>${esc(text || (context === "archiveEmpty" ? "Previous days will appear here as new content is published." : s.description || "Your next 40-day journey will appear here when the first reading is published. We’re glad you’re joining us."))}</p>`;
          break;
        case "archiveList":
          if (context !== "archiveCard")
            html = `<div class="grid archive-grid" style="${archiveGalleryStyle(theme, device === "responsive" ? "" : device)}">${(data.entries || []).map((row: any) => `<article class="card${[...themeBlocks(theme, "archiveCard", "desktop"), ...themeBlocks(theme, "archiveCard", "mobile")].some((b) => b.safeBox) ? " card-artboard" : ""}">${renderBlocks(theme, "archiveCard", { ...data, entry: row }, editor, device === "responsive" ? "" : device)}${renderSafeBoxes(theme, "archiveCard", { ...data, entry: row }, editor, device === "responsive" ? "" : device)}</article>`).join("")}</div>`;
          break;
        case "archiveType":
          if (e)
            html = `<p>${esc(text || (e.type === "media" ? "Listen / Watch" : "Read"))}</p>`;
          break;
        case "archiveDate":
          if (e?.publish_at)
            html = `<p>${esc(text || new Date(e.publish_at).toLocaleDateString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric" }))}</p>`;
          break;
        case "archiveOpen":
          if (e)
            html = `<button class="layout-button" data-id="${esc(e.id)}" aria-label="${esc(e.title)}">${esc(text || "Open reading")}</button>`;
          break;
        case "navigation":
          html = `<nav><a class="button layout-button" href="${esc(b.href || (data.mode === "daily" ? s.archive_url : s.main_url))}">${esc(text || (data.mode === "daily" ? "Previous Days" : "Back to Today’s Reading"))}</a></nav>`;
          break;
        case "status":
          html = `<p class="notice status" role="status">${data.updated ? esc(text || "A new reading is now available.") : ""}</p>`;
          break;
        case "close":
          html = `<button class="layout-button" data-close-reading aria-label="Close reading">${esc(text || "Close ×")}</button>`;
          break;
        case "text":
          html = `<p style="white-space:pre-line">${esc(text)}</p>`;
          break;
        case "button":
          html = b.href
            ? `<a class="layout-button" href="${esc(b.href)}">${esc(text)}</a>`
            : `<span class="layout-button" aria-disabled="true">${esc(text)}${editor ? " (set a link)" : ""}</span>`;
          break;
        case "image":
          if (b.src)
            html = `<img class="layout-image" src="${esc(new URL(b.src, data.origin).href)}" alt="${esc(text)}">`;
          break;
        case "divider":
          html = "<hr>";
          break;
        case "spacer":
          html = '<div aria-hidden="true"></div>';
          break;
        case "rectangle":
        case "ellipse":
        case "triangle":
        case "line":
          html = `<svg class="layout-shape" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">${b.kind === "ellipse" ? '<ellipse cx="50" cy="50" rx="48" ry="48"/>' : b.kind === "triangle" ? '<polygon points="50,2 98,98 2,98"/>' : b.kind === "line" ? '<line x1="0" y1="50" x2="100" y2="50"/>' : '<rect x="2" y="2" width="96" height="96"/>'}</svg>`;
          break;
      }
      if (!html && !editor) return "";
      return `<div class="page-block" data-block="${esc(b.id)}" ${editor ? 'draggable="true" tabindex="0" role="group" aria-label="Select ' + esc(blockLabels[b.kind]) + '"' : ""} style="${esc(blockStyle(b, theme, device))}">${html || `<span class="preview-placeholder">${esc(blockLabels[b.kind])}</span>`}</div>`;
    })
    .join("")}</div>`;
}
